import { z } from "zod";
import {
  COMMERCIAL_TERMS_MODES,
  FREIGHT_MODES,
  MAX_ITEM_DISCOUNT_PCT4,
  MAX_PRICE_CENTS,
  MAX_QUOTE_ITEMS,
  MAX_STOCK_QTY_Q4,
  isIsoDate,
  type CommercialTermsMode,
  type FreightMode,
  type BaseState,
  type StatusTone,
} from "@cx/domain";
import { listQuery } from "./common";
import { blank, text } from "./fields";
import { ulidParam } from "./ids";
import { businessDate, type InstallmentView } from "./prices";
import type { StatusView } from "./statuses";

// ===== Propostas comerciais (RF-VEN-01) =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);
const cents = (what: string) =>
  z
    .number(`Informe ${what}`)
    .int("Use centavos inteiros")
    .min(0, "Valor não pode ser negativo")
    .max(MAX_PRICE_CENTS, "Valor acima do limite");
const pct = z
  .number("Informe o percentual")
  .int("Percentual inválido")
  .min(0, "O desconto vai de 0% a 100%")
  .max(MAX_ITEM_DISCOUNT_PCT4, "O desconto vai de 0% a 100%");
const optDate = blank.pipe(businessDate.nullable());

/**
 * Item: produto cadastrado (`productId`; descrição, código e unidade vêm do cadastro) ou outro item avulso
 * (`productId` nulo, descrição e preço obrigatórios). `listPriceCents` nulo no produto = preço da lista da proposta.
 */
export const quoteItemInput = z
  .object({
    productId: nullish(ulidParam),
    description: text(1000),
    unit: text(6),
    qtyQ4: z
      .number("Informe a quantidade")
      .int("Quantidade inválida")
      .min(1, "A quantidade deve ser maior que zero")
      .max(MAX_STOCK_QTY_Q4, "Quantidade acima do limite"),
    listPriceCents: nullish(cents("o preço")),
    discountPct4: pct.default(0),
  })
  .superRefine((v, ctx) => {
    if (v.productId !== null) return;
    if (!v.description)
      ctx.addIssue({ code: "custom", path: ["description"], message: "Informe a descrição do item" });
    if (v.listPriceCents === null)
      ctx.addIssue({ code: "custom", path: ["listPriceCents"], message: "Informe o preço do item" });
  });
export type QuoteItemInput = z.output<typeof quoteItemInput>;
export type QuoteItemFormValues = z.input<typeof quoteItemInput>;

export const quoteInput = z
  .object({
    date: businessDate,
    validUntil: optDate,
    contactId: ulidParam,
    attentionOf: text(120),
    sellerId: nullish(ulidParam),
    priceListId: nullish(ulidParam),
    introduction: text(5000),
    nextContactAt: optDate,
    items: z
      .array(quoteItemInput, "Informe os itens")
      .min(1, "Inclua ao menos um item")
      .max(MAX_QUOTE_ITEMS, `No máximo ${MAX_QUOTE_ITEMS} itens`),
    /** Desconto geral em % sobre o subtotal; null = em valor (`discountCents`). */
    discountPct4: nullish(pct),
    discountCents: z
      .number("Informe o desconto")
      .int("Use centavos inteiros")
      .min(0, "Valor não pode ser negativo")
      .default(0),
    freightCents: cents("o frete").default(0),
    otherExpensesCents: cents("as outras despesas").default(0),
    commercialTermsMode: z.enum(COMMERCIAL_TERMS_MODES, "Condição comercial inválida").default("none"),
    paymentTermId: nullish(ulidParam),
    commercialTermsText: text(2000),
    generalTerms: text(5000),
    carrierId: nullish(ulidParam),
    freightMode: z.literal(FREIGHT_MODES, "Modalidade do frete inválida").default(9),
  })
  .superRefine((v, ctx) => {
    if (v.commercialTermsMode === "installments" && !v.paymentTermId)
      ctx.addIssue({ code: "custom", path: ["paymentTermId"], message: "Escolha a condição de pagamento" });
    if (v.commercialTermsMode === "text" && !v.commercialTermsText)
      ctx.addIssue({
        code: "custom",
        path: ["commercialTermsText"],
        message: "Escreva a condição comercial",
      });
    // Zod 4 roda o refinamento mesmo com a data inválida: só compara datas válidas.
    if (v.validUntil && isIsoDate(v.validUntil) && isIsoDate(v.date) && v.validUntil < v.date)
      ctx.addIssue({
        code: "custom",
        path: ["validUntil"],
        message: "A validade deve ser igual ou posterior à data",
      });
  });
export type QuoteInput = z.output<typeof quoteInput>;
export type QuoteFormValues = z.input<typeof quoteInput>;

export const quoteListQuery = listQuery.extend({
  statusId: ulidParam.optional(),
  sellerId: ulidParam.optional(),
  from: businessDate.optional(),
  to: businessDate.optional(),
});
export type QuoteListQuery = z.output<typeof quoteListQuery>;

/** Mudança de situação da proposta: a situação que a tela mostrava é obrigatória (409 `status_changed`). */
export const quoteStatusInput = z.object({ toStatusId: ulidParam, expectedFromStatusId: ulidParam });
export type QuoteStatusInput = z.output<typeof quoteStatusInput>;

export type QuoteStatusRef = { id: string; label: string; color: StatusTone; baseState: BaseState };

/** Item calculado. Nunca traz custo nem o % da lista: só o preço de venda usado. */
export type QuoteItemView = {
  position: number;
  productId: string | null;
  description: string;
  code: string | null;
  unit: string | null;
  qtyQ4: number;
  listPriceCents: number;
  discountPct4: number;
  unitPriceCents: number;
  totalCents: number;
};

/** Cálculo da proposta (prévia e parte da visão). */
export type QuoteCalc = {
  items: QuoteItemView[];
  itemsTotalCents: number;
  otherItemsTotalCents: number;
  itemsDiscountCents: number;
  discountPct4: number | null;
  discountCents: number;
  freightCents: number;
  otherExpensesCents: number;
  totalCents: number;
  itemsCount: number;
  quantitySumQ4: number;
  /** Só com condição comercial "Parcelas". */
  installments: InstallmentView[];
};

export type QuoteView = QuoteCalc & {
  id: string;
  number: number;
  date: string;
  validUntil: string | null;
  contact: { id: string; name: string; document: string | null };
  attentionOf: string | null;
  seller: { id: string; name: string } | null;
  priceList: { id: string; name: string } | null;
  introduction: string | null;
  nextContactAt: string | null;
  commercialTermsMode: CommercialTermsMode;
  paymentTerm: { id: string; name: string } | null;
  commercialTermsText: string | null;
  generalTerms: string | null;
  carrier: { id: string; name: string } | null;
  freightMode: FreightMode;
  status: QuoteStatusRef;
  /** Situações para onde há transição ativa a partir da atual. */
  nextStatuses: QuoteStatusRef[];
  convertedOrderId: string | null;
  convertedAt: string | null;
  /** Há conversor de pedidos (T1.8), a proposta não foi convertida e não está cancelada. */
  canConvert: boolean;
  createdAt: string;
  updatedAt: string;
};

export type QuoteListItem = {
  id: string;
  number: number;
  date: string;
  validUntil: string | null;
  contactName: string;
  sellerName: string | null;
  totalCents: number;
  status: QuoteStatusRef;
  convertedOrderId: string | null;
};

/** `totalCents` só na primeira página (null nas seguintes ou acima de 2^53). */
export type QuoteListResult = {
  data: QuoteListItem[];
  nextCursor: string | null;
  summary: { count: number; totalCents: number | null };
};

/** Opções do formulário (sem % de lista nem custo). Usuário-vendedor vê só a si mesmo como vendedor. */
export type QuoteFormOptions = {
  priceLists: { id: string; name: string; active: boolean }[];
  paymentTerms: { id: string; name: string; active: boolean }[];
  sellers: { id: string; name: string }[];
  /** Usuário-vendedor: o vendedor da proposta é sempre ele (o servidor força). */
  sellerLocked: boolean;
  carriers: { id: string; name: string }[];
  statuses: StatusView[];
  /** Usuário-vendedor sem "incluir produto não cadastrado" não inclui outros itens. */
  canAddOtherItems: boolean;
};

export type QuoteConvertResult = { orderId: string; alreadyConverted: boolean };
export type QuotePdfLink = { url: string; fileName: string };

/** Dados do PDF (montados no DO, desenhados no Worker). Endereços já em uma linha. */
export type QuotePrintData = {
  quote: QuoteView;
  company: {
    name: string;
    tradeName: string | null;
    document: string | null;
    address: string | null;
    phone: string | null;
    email: string | null;
  };
  contact: {
    name: string;
    document: string | null;
    address: string | null;
    phone: string | null;
    email: string | null;
  };
};
